// src/components/DisputeAdminPanel.js
// ============================================================================
// ADMIN PANEL FOR MANAGING MATCH DISPUTES
// 
// Shows pending disputes with match evidence and allows admins to:
// - View dispute details and reason
// - Review VOD/stream evidence
// - Confirm original result
// - Change winner
// - Order rematch
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView,
  ActivityIndicator, Modal, TextInput, Alert, Linking,
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';

// ============================================================================
// THEME
// ============================================================================
const COLORS = {
  GOLD: '#FCD000',
  BG: '#0b0b0b',
  SURFACE: '#111111',
  SURFACE2: '#1a1a1a',
  TEXT: '#FFFFFF',
  DIM: '#888888',
  BORDER: '#333333',
  SUCCESS: '#2ECC71',
  DANGER: '#E74C3C',
  WARNING: '#F39C12',
  BLUE: '#3498DB',
};

const DISPUTE_REASONS = {
  'wrong_winner': { label: 'Wrong Winner', icon: '❌', color: COLORS.DANGER },
  'cheating': { label: 'Cheating', icon: '🚫', color: COLORS.DANGER },
  'disconnection': { label: 'Disconnection', icon: '📡', color: COLORS.WARNING },
  'rule_violation': { label: 'Rule Violation', icon: '⚠️', color: COLORS.WARNING },
  'other': { label: 'Other', icon: '❓', color: COLORS.DIM },
};

// Advisory recommendation written onto the alert doc by the AI adjudicator
// backend (aiReview field). Purely informational — the buttons below are
// still the only way anything gets resolved.
const AI_RECOMMENDATION_LABELS = {
  'original_winner_confirmed': { label: 'Confirm original result', color: COLORS.SUCCESS },
  'winner_changed': { label: 'Change the winner', color: COLORS.WARNING },
  'rematch_ordered': { label: 'Order a rematch', color: COLORS.BLUE },
  'escalate_to_founder': { label: 'Needs your judgment', color: COLORS.DIM },
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function DisputeAdminPanel({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [disputes, setDisputes] = useState([]);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  const functions = getFunctions();

  // Load pending disputes
  const loadDisputes = useCallback(async () => {
    setLoading(true);
    try {
      const getPendingDisputes = httpsCallable(functions, 'getPendingDisputes');
      const result = await getPendingDisputes();
      
      if (result.data.success) {
        setDisputes(result.data.disputes || []);
      }
    } catch (error) {
      console.error('Failed to load disputes:', error);
      Alert.alert('Error', 'Failed to load disputes');
    } finally {
      setLoading(false);
    }
  }, [functions]);

  useEffect(() => {
    loadDisputes();
  }, [loadDisputes]);

  // Resolve dispute
  const handleResolve = async (outcome, newWinnerId = null) => {
    if (!selectedDispute) return;

    const confirmMessages = {
      'original_winner_confirmed': 'Confirm the original result stands?',
      'winner_changed': `Change winner to ${newWinnerId === selectedDispute.matchInfo?.p1 ? 'P1' : 'P2'}?`,
      'rematch_ordered': 'Order a rematch between these players?',
    };

    Alert.alert(
      'Confirm Resolution',
      confirmMessages[outcome] || 'Resolve this dispute?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Confirm',
          style: 'destructive',
          onPress: async () => {
            setResolving(true);
            try {
              const resolveDispute = httpsCallable(functions, 'resolveDispute');
              const result = await resolveDispute({
                matchId: selectedDispute.matchId,
                eventId: selectedDispute.eventId,
                outcome,
                newWinnerId,
                adminNotes,
              });

              if (result.data.success) {
                Alert.alert('Success', 'Dispute resolved');
                setSelectedDispute(null);
                setAdminNotes('');
                loadDisputes();
              }
            } catch (error) {
              console.error('Failed to resolve dispute:', error);
              Alert.alert('Error', error.message || 'Failed to resolve dispute');
            } finally {
              setResolving(false);
            }
          },
        },
      ]
    );
  };

  // ============================================================================
  // RENDER: Dispute List
  // ============================================================================
  const renderDisputeCard = (dispute) => {
    const reasonInfo = DISPUTE_REASONS[dispute.dispute?.reason] || DISPUTE_REASONS.other;
    const filedAt = dispute.dispute?.filedAt?.toDate?.() || new Date(dispute.createdAt?._seconds * 1000);
    
    return (
      <TouchableOpacity
        key={dispute.id}
        style={styles.disputeCard}
        onPress={() => setSelectedDispute(dispute)}
      >
        <View style={styles.cardHeader}>
          <View style={styles.cardTitleRow}>
            <Text style={styles.cardIcon}>{reasonInfo.icon}</Text>
            <View>
              <Text style={styles.cardTitle}>{dispute.eventName}</Text>
              <Text style={styles.cardSubtitle}>
                {dispute.matchInfo?.roundName || `Round ${dispute.matchInfo?.round}`}
              </Text>
            </View>
          </View>
          <View style={[styles.reasonBadge, { backgroundColor: reasonInfo.color + '20', borderColor: reasonInfo.color }]}>
            <Text style={[styles.reasonText, { color: reasonInfo.color }]}>{reasonInfo.label}</Text>
          </View>
        </View>

        <View style={styles.cardBody}>
          <View style={styles.playersRow}>
            <Text style={styles.playerText}>
              {dispute.matchInfo?.p1 || 'Player 1'} vs {dispute.matchInfo?.p2 || 'Player 2'}
            </Text>
          </View>
          
          <View style={styles.metaRow}>
            <Text style={styles.metaText}>
              Filed by: {dispute.dispute?.filedByName || 'Unknown'}
            </Text>
            <Text style={styles.metaText}>
              {formatTimeAgo(filedAt)}
            </Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  // ============================================================================
  // RENDER: Dispute Detail Modal
  // ============================================================================
  const renderDetailModal = () => {
    if (!selectedDispute) return null;

    const dispute = selectedDispute;
    const reasonInfo = DISPUTE_REASONS[dispute.dispute?.reason] || DISPUTE_REASONS.other;
    
    // Get player IDs for winner selection
    const p1Id = dispute.matchInfo?.p1;
    const p2Id = dispute.matchInfo?.p2;
    const originalWinnerId = dispute.dispute?.originalWinnerId;

    return (
      <Modal visible={true} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <ScrollView showsVerticalScrollIndicator={false}>
              {/* Header */}
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>Dispute Details</Text>
                <TouchableOpacity onPress={() => setSelectedDispute(null)}>
                  <Text style={styles.closeBtn}>✕</Text>
                </TouchableOpacity>
              </View>

              {/* Event Info */}
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Event</Text>
                <Text style={styles.eventName}>{dispute.eventName}</Text>
                <Text style={styles.roundName}>
                  {dispute.matchInfo?.roundName || `Round ${dispute.matchInfo?.round}`}
                </Text>
              </View>

              {/* Players */}
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Match</Text>
                <View style={styles.playersBox}>
                  <View style={styles.playerBox}>
                    <Text style={styles.playerLabel}>P1</Text>
                    <Text style={styles.playerName}>{dispute.matchInfo?.p1 || 'Player 1'}</Text>
                    {originalWinnerId === p1Id && (
                      <Text style={styles.winnerTag}>Original Winner</Text>
                    )}
                  </View>
                  <Text style={styles.vsText}>vs</Text>
                  <View style={styles.playerBox}>
                    <Text style={styles.playerLabel}>P2</Text>
                    <Text style={styles.playerName}>{dispute.matchInfo?.p2 || 'Player 2'}</Text>
                    {originalWinnerId === p2Id && (
                      <Text style={styles.winnerTag}>Original Winner</Text>
                    )}
                  </View>
                </View>
              </View>

              {/* Dispute Info */}
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Dispute</Text>
                <View style={[styles.reasonBox, { borderColor: reasonInfo.color }]}>
                  <Text style={styles.reasonIcon}>{reasonInfo.icon}</Text>
                  <View>
                    <Text style={[styles.reasonLabel, { color: reasonInfo.color }]}>
                      {reasonInfo.label}
                    </Text>
                    <Text style={styles.filedBy}>
                      Filed by {dispute.dispute?.filedByName}
                    </Text>
                  </View>
                </View>
                
                {dispute.dispute?.description && (
                  <View style={styles.descriptionBox}>
                    <Text style={styles.descriptionLabel}>Player's Description:</Text>
                    <Text style={styles.descriptionText}>{dispute.dispute.description}</Text>
                  </View>
                )}
              </View>

              {/* AI Review (advisory) */}
              {dispute.aiReview && (() => {
                const rec = AI_RECOMMENDATION_LABELS[dispute.aiReview.recommendation]
                  || { label: dispute.aiReview.recommendation, color: COLORS.DIM };
                return (
                  <View style={styles.section}>
                    <Text style={styles.sectionTitle}>AI Review (Advisory)</Text>
                    <View style={[styles.aiReviewBox, { borderColor: rec.color }]}>
                      <Text style={[styles.aiRecommendation, { color: rec.color }]}>
                        {rec.label}
                        {Number.isFinite(dispute.aiReview.confidence)
                          ? `  ·  ${Math.round(dispute.aiReview.confidence * 100)}% confidence`
                          : ''}
                      </Text>
                      {!!dispute.aiReview.reasoning && (
                        <Text style={styles.aiReasoning}>{dispute.aiReview.reasoning}</Text>
                      )}
                      {(dispute.aiReview.evidenceSummary || []).map((line, i) => (
                        <Text key={i} style={styles.aiEvidenceLine}>• {line}</Text>
                      ))}
                      <Text style={styles.aiDisclaimer}>
                        Advisory only — your resolution below is final.
                      </Text>
                    </View>
                  </View>
                );
              })()}

              {/* Evidence Links */}
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Evidence</Text>
                <TouchableOpacity 
                  style={styles.evidenceBtn}
                  onPress={() => Linking.openURL(`https://critcash.com/admin/match/${dispute.matchId}`)}
                >
                  <Text style={styles.evidenceBtnText}>📹 View Match VODs</Text>
                </TouchableOpacity>
                <TouchableOpacity 
                  style={styles.evidenceBtn}
                  onPress={() => Linking.openURL(`https://critcash.com/event/${dispute.eventId}`)}
                >
                  <Text style={styles.evidenceBtnText}>🏆 View Event</Text>
                </TouchableOpacity>
              </View>

              {/* Admin Notes */}
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Admin Notes</Text>
                <TextInput
                  style={styles.notesInput}
                  placeholder="Add notes about your decision..."
                  placeholderTextColor={COLORS.DIM}
                  value={adminNotes}
                  onChangeText={setAdminNotes}
                  multiline
                  numberOfLines={3}
                />
              </View>

              {/* Resolution Actions */}
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Resolution</Text>
                
                <TouchableOpacity
                  style={[styles.actionBtn, styles.confirmBtn]}
                  onPress={() => handleResolve('original_winner_confirmed')}
                  disabled={resolving}
                >
                  <Text style={styles.actionBtnText}>
                    ✅ Confirm Original Result
                  </Text>
                  <Text style={styles.actionBtnSubtext}>
                    Keep {originalWinnerId === p1Id ? 'P1' : 'P2'} as winner
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.actionBtn, styles.changeBtn]}
                  onPress={() => handleResolve('winner_changed', originalWinnerId === p1Id ? p2Id : p1Id)}
                  disabled={resolving}
                >
                  <Text style={styles.actionBtnText}>
                    🔄 Change Winner
                  </Text>
                  <Text style={styles.actionBtnSubtext}>
                    Award win to {originalWinnerId === p1Id ? 'P2' : 'P1'}
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.actionBtn, styles.rematchBtn]}
                  onPress={() => handleResolve('rematch_ordered')}
                  disabled={resolving}
                >
                  <Text style={styles.actionBtnText}>
                    🔁 Order Rematch
                  </Text>
                  <Text style={styles.actionBtnSubtext}>
                    Both players must play again
                  </Text>
                </TouchableOpacity>
              </View>

              {resolving && (
                <View style={styles.loadingOverlay}>
                  <ActivityIndicator size="large" color={COLORS.GOLD} />
                  <Text style={styles.loadingText}>Resolving...</Text>
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={onClose}>
          <Text style={styles.backBtn}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.title}>Match Disputes</Text>
        <TouchableOpacity onPress={loadDisputes}>
          <Text style={styles.refreshBtn}>↻</Text>
        </TouchableOpacity>
      </View>

      {/* Content */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.GOLD} />
          <Text style={styles.loadingText}>Loading disputes...</Text>
        </View>
      ) : disputes.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyIcon}>✅</Text>
          <Text style={styles.emptyText}>No pending disputes</Text>
          <Text style={styles.emptySubtext}>All caught up!</Text>
        </View>
      ) : (
        <ScrollView style={styles.list} showsVerticalScrollIndicator={false}>
          <Text style={styles.listHeader}>{disputes.length} Pending Dispute{disputes.length !== 1 ? 's' : ''}</Text>
          {disputes.map(renderDisputeCard)}
        </ScrollView>
      )}

      {renderDetailModal()}
    </View>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function formatTimeAgo(date) {
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return date.toLocaleDateString();
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.BG,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.BORDER,
  },
  backBtn: {
    color: COLORS.GOLD,
    fontSize: 16,
    fontWeight: '600',
  },
  title: {
    color: COLORS.TEXT,
    fontSize: 18,
    fontWeight: '800',
  },
  refreshBtn: {
    color: COLORS.GOLD,
    fontSize: 24,
  },
  
  // Loading & Empty
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: COLORS.DIM,
    marginTop: 12,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  emptyText: {
    color: COLORS.TEXT,
    fontSize: 18,
    fontWeight: '600',
  },
  emptySubtext: {
    color: COLORS.DIM,
    marginTop: 4,
  },
  
  // List
  list: {
    flex: 1,
    padding: 16,
  },
  listHeader: {
    color: COLORS.DIM,
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  
  // Dispute Card
  disputeCard: {
    backgroundColor: COLORS.SURFACE,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  cardTitle: {
    color: COLORS.TEXT,
    fontSize: 16,
    fontWeight: '700',
  },
  cardSubtitle: {
    color: COLORS.DIM,
    fontSize: 13,
  },
  reasonBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  reasonText: {
    fontSize: 12,
    fontWeight: '600',
  },
  cardBody: {},
  playersRow: {
    marginBottom: 8,
  },
  playerText: {
    color: COLORS.TEXT,
    fontSize: 14,
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  metaText: {
    color: COLORS.DIM,
    fontSize: 12,
  },
  
  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.8)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: COLORS.SURFACE,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
    padding: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    color: COLORS.TEXT,
    fontSize: 20,
    fontWeight: '800',
  },
  closeBtn: {
    color: COLORS.DIM,
    fontSize: 24,
    padding: 4,
  },
  
  // Sections
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    color: COLORS.DIM,
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  eventName: {
    color: COLORS.TEXT,
    fontSize: 18,
    fontWeight: '700',
  },
  roundName: {
    color: COLORS.GOLD,
    fontSize: 14,
    fontWeight: '600',
  },
  
  // Players Box
  playersBox: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: COLORS.BG,
    borderRadius: 12,
    padding: 16,
  },
  playerBox: {
    flex: 1,
    alignItems: 'center',
  },
  playerLabel: {
    color: COLORS.DIM,
    fontSize: 11,
    fontWeight: '600',
  },
  playerName: {
    color: COLORS.TEXT,
    fontSize: 14,
    fontWeight: '700',
    marginTop: 4,
  },
  winnerTag: {
    color: COLORS.SUCCESS,
    fontSize: 11,
    marginTop: 4,
  },
  vsText: {
    color: COLORS.DIM,
    fontSize: 14,
    fontWeight: '600',
    marginHorizontal: 12,
  },
  
  // Reason Box
  reasonBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.BG,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
  },
  reasonIcon: {
    fontSize: 32,
    marginRight: 16,
  },
  reasonLabel: {
    fontSize: 16,
    fontWeight: '700',
  },
  filedBy: {
    color: COLORS.DIM,
    fontSize: 13,
    marginTop: 2,
  },
  
  // Description
  descriptionBox: {
    backgroundColor: COLORS.BG,
    borderRadius: 8,
    padding: 12,
    marginTop: 12,
  },
  descriptionLabel: {
    color: COLORS.DIM,
    fontSize: 11,
    marginBottom: 4,
  },
  descriptionText: {
    color: COLORS.TEXT,
    fontSize: 14,
    lineHeight: 20,
  },
  
  // AI Review
  aiReviewBox: {
    backgroundColor: COLORS.BG,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
  },
  aiRecommendation: {
    fontSize: 15,
    fontWeight: '700',
    marginBottom: 8,
  },
  aiReasoning: {
    color: COLORS.TEXT,
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 8,
  },
  aiEvidenceLine: {
    color: COLORS.DIM,
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 2,
  },
  aiDisclaimer: {
    color: COLORS.DIM,
    fontSize: 11,
    marginTop: 8,
    fontStyle: 'italic',
  },

  // Evidence
  evidenceBtn: {
    backgroundColor: COLORS.BG,
    borderRadius: 8,
    padding: 14,
    alignItems: 'center',
    marginBottom: 8,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
  },
  evidenceBtnText: {
    color: COLORS.TEXT,
    fontSize: 14,
    fontWeight: '600',
  },
  
  // Notes
  notesInput: {
    backgroundColor: COLORS.BG,
    borderRadius: 8,
    padding: 12,
    color: COLORS.TEXT,
    fontSize: 14,
    minHeight: 80,
    textAlignVertical: 'top',
    borderWidth: 1,
    borderColor: COLORS.BORDER,
  },
  
  // Action Buttons
  actionBtn: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 10,
    alignItems: 'center',
  },
  confirmBtn: {
    backgroundColor: COLORS.SUCCESS + '20',
    borderWidth: 1,
    borderColor: COLORS.SUCCESS,
  },
  changeBtn: {
    backgroundColor: COLORS.WARNING + '20',
    borderWidth: 1,
    borderColor: COLORS.WARNING,
  },
  rematchBtn: {
    backgroundColor: COLORS.BLUE + '20',
    borderWidth: 1,
    borderColor: COLORS.BLUE,
  },
  actionBtnText: {
    color: COLORS.TEXT,
    fontSize: 16,
    fontWeight: '700',
  },
  actionBtnSubtext: {
    color: COLORS.DIM,
    fontSize: 12,
    marginTop: 4,
  },
  
  // Loading overlay
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 20,
  },
});